'use client';

import { useMemo, useState, useEffect } from 'react';
import { OrderStatusEnum, type Order } from '../lib/types';
import firebase from 'firebase/compat/app';
import { normalizeOrderProducts } from '../lib/orderProducts';
import { useAppSelector } from 'hooks';
import { charlotteUid, isAdmin, michaelUid } from 'components/AdminOnly';

type UseOrdersFilters = {
  creatorId?: string;
  orgId?: string;
  limit?: number | null;
  enabled?: boolean;
  /** Prognosis needs all creators' orders for organizations the caller belongs to. */
  completeHistory?: boolean;
};

type OrderSnapshot = {
  id: string;
  data: firebase.firestore.DocumentData;
};

function toDate(value: unknown): Date | undefined {
  if (value instanceof Date && Number.isFinite(value.getTime())) {
    return value;
  }
  if (typeof (value as { toDate?: unknown })?.toDate === 'function') {
    const parsed = (value as { toDate: () => Date }).toDate();
    return parsed instanceof Date && Number.isFinite(parsed.getTime()) ? parsed : undefined;
  }
  return undefined;
}

export function useOrders(filters: UseOrdersFilters = {}) {
  const [orderSnapshots, setOrderSnapshots] = useState<OrderSnapshot[]>([]);
  const [localOrderOverrides, setLocalOrderOverrides] = useState<Record<string, Order>>({});
  const [localDeletedOrderIds, setLocalDeletedOrderIds] = useState<Set<string>>(() => new Set());
  const productMap = useAppSelector((state) => state.products.productsMap);
  const orgMap = useAppSelector((state) => state.orgs.orgMap);
  const userId = useAppSelector((state) => state.user.details.uid);
  const isUserAdmin = isAdmin(userId);
  const isUserMichael = userId === michaelUid;
  const { creatorId, orgId, limit = 100, enabled = true, completeHistory = false } = filters;
  // Match isOrgMember in firestore.rules; group membership alone is not sufficient.
  const memberOrgIdsKey = JSON.stringify(
    completeHistory && !isUserAdmin
      ? Object.values(orgMap)
          .filter((org) => org.members?.[userId]?.uid === userId && (!orgId || org.id === orgId))
          .map((org) => org.id)
          .sort()
      : []
  );
  const queryKey = JSON.stringify({
    userId,
    access: isUserAdmin ? 'admin' : isUserMichael ? 'michael-and-charlotte' : 'creator',
    creatorId: creatorId ?? null,
    orgId: orgId ?? null,
    limit,
    completeHistory,
    memberOrgIdsKey,
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const [fromCache, setFromCache] = useState(false);
  const [loadedQueryKey, setLoadedQueryKey] = useState<string | null>(null);

  const { orders, invalidOrderCount, invalidHistoryOrgIds } = useMemo(() => {
    let invalidCount = 0;
    const invalidOrgs = new Set<string>();
    const mappedOrders = orderSnapshots
      .filter(({ id }) => !localDeletedOrderIds.has(id))
      .map(({ id, data }) => {
        const createTime = toDate(data.createTime);
        if (!createTime || typeof data.orgId !== 'string' || !data.orgId) {
          invalidCount += 1;
          // Quarantine orphan records in the global warning. Only a known owner
          // can have its history invalidated; an orphan must not disable every customer.
          if (typeof data.orgId === 'string' && data.orgId) invalidOrgs.add(data.orgId);
          return undefined;
        }
        const deprecatedOrder =
          (data.products == null || (Array.isArray(data.products) && data.products.length === 0)) &&
          typeof data.productId === 'string';
        const normalizedProducts = normalizeOrderProducts(
          deprecatedOrder
            ? [
                {
                  id: data.productId,
                  name: productMap[data.productId]?.title,
                  quantity: data.amount,
                  unit: '1',
                },
              ]
            : data.products
        );
        const requestedDeliveryDate = toDate(data.delivery?.date);

        return {
          // TEMPORARY FIX: remove this when migrated all orders
          isDeprecatedOrder: deprecatedOrder,
          id,
          createTime,
          products: normalizedProducts.products,
          productDataIncomplete: normalizedProducts.incomplete,
          deliveryDate: requestedDeliveryDate,
          deliveryDateIsFallback: !requestedDeliveryDate,
          deliveryAddress: data.delivery?.address,
          deliveryZip: data.delivery?.zip,
          deliveryCity: data.delivery?.city,
          deliveryAtt: data.delivery?.att,
          contactPerson: data.contact?.name,
          nextExpectedDeliveryDate: toDate(data.delivery?.nextExpectedDeliveryDate),
          orderRef: data.orderRef,
          terms: data.terms || '',
          returnedContainers: data.returnedContainers || [],
          internalNote: data.internalNote || '',
          invoiceEmail: data.invoiceEmail || '',
          eanNumber: data.eanNumber || '',
          cvrNumber: data.cvrNumber || '',
          companyName: deprecatedOrder ? orgMap[data.orgId]?.title : data.companyName,
          orgId: data.orgId,
          status: data.status,
          createdBy: {
            id: data.createdBy?.id,
            displayName: deprecatedOrder
              ? orgMap[data.orgId]?.members?.[data?.userId]?.name ||
                orgMap[data.orgId]?.members?.[data?.userId]?.email ||
                orgMap[data.orgId]?.members?.[data?.userId]?.uid
              : data.createdBy?.displayName,
          },
          intermediateStorage:
            data.intermediateStorage ??
            (data.isIntermediateStorage === true ? 'legacy' : undefined),
          invoiceNo: data.invoiceNo,
        } as Order;
      })
      .filter((order): order is Order => order !== undefined);

    const overrides = Object.values(localOrderOverrides);
    if (overrides.length === 0) {
      return {
        orders: mappedOrders,
        invalidOrderCount: invalidCount,
        invalidHistoryOrgIds: Array.from(invalidOrgs),
      };
    }

    const mappedById = new Map(mappedOrders.map((order) => [order.id, order]));
    overrides.forEach((order) => {
      mappedById.set(order.id, order);
    });
    return {
      orders: Array.from(mappedById.values()),
      invalidOrderCount: invalidCount,
      invalidHistoryOrgIds: Array.from(invalidOrgs),
    };
  }, [localDeletedOrderIds, localOrderOverrides, orderSnapshots, orgMap, productMap]);

  useEffect(() => {
    if (!enabled) {
      setOrderSnapshots([]);
      setLocalOrderOverrides({});
      setLocalDeletedOrderIds(new Set());
      setLoading(false);
      setError(null);
      setUpdatedAt(null);
      setFromCache(false);
      setLoadedQueryKey(null);
      return undefined;
    }

    setLoading(true);
    setError(null);

    const onError = (error: unknown) => {
      console.error(error);
      setOrderSnapshots([]);
      setLocalOrderOverrides({});
      setLocalDeletedOrderIds(new Set());
      setError(error instanceof Error ? error : new Error('Ukendt fejl ved indlæsning af ordrer'));
      setUpdatedAt(null);
      setFromCache(false);
      setLoading(false);
      setLoadedQueryKey(queryKey);
    };

    if (completeHistory) {
      const memberOrgIds: string[] = JSON.parse(memberOrgIdsKey);
      const collection = firebase.firestore().collection('orders');
      const queries: firebase.firestore.Query[] = [];
      if (isUserAdmin) {
        queries.push(orgId ? collection.where('orgId', '==', orgId) : collection);
      } else {
        // Ten org lookups at most per query, within the rules document-access budget.
        for (let index = 0; index < memberOrgIds.length; index += 10) {
          queries.push(collection.where('orgId', 'in', memberOrgIds.slice(index, index + 10)));
        }
      }
      let active = true;
      let failed = false;
      const snapshots: Array<firebase.firestore.QuerySnapshot | undefined> = queries.map(
        () => undefined
      );
      const publish = () => {
        if (!active || failed || snapshots.some((snapshot) => !snapshot)) return;
        // No orderBy/limit: missing createTime must reach validation, not disappear from history.
        const rows = snapshots.flatMap((snapshot) =>
          snapshot!.docs.map((doc) => ({ id: doc.id, data: doc.data() }))
        );
        rows.sort(
          (a, b) =>
            (toDate(b.data.createTime)?.getTime() ?? 0) -
            (toDate(a.data.createTime)?.getTime() ?? 0)
        );
        setOrderSnapshots(rows);
        setLocalOrderOverrides({});
        setLocalDeletedOrderIds(new Set());
        setError(null);
        setUpdatedAt(new Date());
        setFromCache(
          snapshots.some(
            (snapshot) => snapshot!.metadata.fromCache || snapshot!.metadata.hasPendingWrites
          )
        );
        setLoading(false);
        setLoadedQueryKey(queryKey);
      };
      const unsubscribers = queries.map((query, index) =>
        query.onSnapshot(
          { includeMetadataChanges: true },
          (snapshot) => {
            snapshots[index] = snapshot;
            publish();
          },
          (error) => {
            if (active) {
              failed = true;
              onError(error);
            }
          }
        )
      );
      publish(); // Empty membership is a loaded empty result, never an unrestricted query.
      return () => {
        active = false;
        unsubscribers.forEach((unsubscribe) => unsubscribe());
      };
    }

    let query = firebase.firestore().collection('orders') as firebase.firestore.Query;

    if (isUserAdmin) {
      if (creatorId) {
        query = query.where('createdBy.id', '==', creatorId);
      }
    } else if (isUserMichael) {
      if (creatorId) {
        query = query.where('createdBy.id', '==', creatorId);
      } else {
        query = query.where('createdBy.id', 'in', [michaelUid, charlotteUid]);
      }
    } else {
      query = query.where('createdBy.id', '==', userId);
    }

    if (orgId) {
      query = query.where('orgId', '==', orgId);
    }

    query = query.orderBy('createTime', 'desc');
    if (limit !== null) {
      query = query.limit(limit);
    }

    return query.onSnapshot((snapshot) => {
      setOrderSnapshots(snapshot.docs.map((doc) => ({ id: doc.id, data: doc.data() })));
      setLocalOrderOverrides({});
      setLocalDeletedOrderIds(new Set());
      setError(null);
      setUpdatedAt(new Date());
      setFromCache(snapshot.metadata.fromCache);
      setLoading(false);
      setLoadedQueryKey(queryKey);
    }, onError);
  }, [
    completeHistory,
    memberOrgIdsKey,
    creatorId,
    enabled,
    isUserAdmin,
    isUserMichael,
    limit,
    orgId,
    queryKey,
    userId,
  ]);

  const addOrder = (order: Order) => {
    setLocalDeletedOrderIds((prev) => {
      if (!prev.has(order.id)) {
        return prev;
      }

      const next = new Set(prev);
      next.delete(order.id);
      return next;
    });
    setLocalOrderOverrides((prev) => ({ ...prev, [order.id]: order }));
  };

  const updateOrder = (updatedOrder: Order) => {
    setLocalDeletedOrderIds((prev) => {
      if (!prev.has(updatedOrder.id)) {
        return prev;
      }

      const next = new Set(prev);
      next.delete(updatedOrder.id);
      return next;
    });
    setLocalOrderOverrides((prev) => ({ ...prev, [updatedOrder.id]: updatedOrder }));
  };

  const deleteOrder = (orderId: string) => {
    setLocalDeletedOrderIds((prev) => {
      const next = new Set(prev);
      next.add(orderId);
      return next;
    });
    setLocalOrderOverrides((prev) => {
      if (!prev[orderId]) {
        return prev;
      }

      const next = { ...prev };
      delete next[orderId];
      return next;
    });
  };

  const hasLoadedCurrentQuery = enabled && loadedQueryKey === queryKey;
  const incompleteHistoryOrgIds = useMemo(() => {
    const members = new Set<string>(JSON.parse(memberOrgIdsKey));
    const candidates = new Set([...Object.keys(orgMap), ...orders.map((order) => order.orgId)]);
    return Array.from(
      new Set([
        ...invalidHistoryOrgIds,
        ...Array.from(candidates).filter(
          (id) =>
            !hasLoadedCurrentQuery ||
            fromCache ||
            (completeHistory
              ? !isUserAdmin && !members.has(id)
              : !isUserAdmin || Boolean(creatorId) || limit !== null)
        ),
      ])
    );
  }, [
    memberOrgIdsKey,
    orgMap,
    orders,
    invalidHistoryOrgIds,
    hasLoadedCurrentQuery,
    fromCache,
    completeHistory,
    isUserAdmin,
    creatorId,
    limit,
  ]);

  return {
    orders: hasLoadedCurrentQuery ? orders : [],
    addOrder,
    updateOrder,
    deleteOrder,
    loading: enabled && (!hasLoadedCurrentQuery || (!error && (loading || updatedAt === null))),
    error: hasLoadedCurrentQuery ? error : null,
    updatedAt: hasLoadedCurrentQuery ? updatedAt : null,
    fromCache: hasLoadedCurrentQuery ? fromCache : false,
    invalidOrderCount: hasLoadedCurrentQuery ? invalidOrderCount : 0,
    incompleteHistoryOrgIds,
  };
}

// utility function
export const getMostRecentOrderWithApproachingDelivery = (orders: Order[]): Order[] => {
  const now = new Date();
  const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

  // First, group orders by orgId and get the most recent for each organization
  const mostRecentOrdersByOrg = orders.reduce((acc, order) => {
    if (!acc[order.orgId] || new Date(order.createTime) > new Date(acc[order.orgId].createTime)) {
      acc[order.orgId] = order;
    }
    return acc;
  }, {} as Record<string, Order>);

  // Then filter the most recent orders to only include those with approaching/overdue delivery dates
  const eligibleOrders = Object.values(mostRecentOrdersByOrg).filter((order) => {
    if (!order.nextExpectedDeliveryDate || order.status === OrderStatusEnum.VOIDED) {
      return false;
    }

    // Check if delivery date is within 7 days or overdue (past due)
    return order.nextExpectedDeliveryDate <= sevenDaysFromNow;
  });

  return eligibleOrders;
};
